import { FC, ReactNode } from 'react';
import './PageWrapper.scss';
import clsx from 'clsx';
import ContentSurface from '../ContentSurface';

export interface IPageWrapperProps {
    /** Элемент */
    children?: ReactNode;
    /** Элемент кнопок */
    actions?: ReactNode;
    /** Заголовок */
    title?: string | ReactNode;
    /**
     * Без строки заголовка (если нет и `titleAside`): страница сама показывает, где она, место отдаётся содержимому.
     * @default false
     */
    hideTitle?: boolean;
    /** Элементы в строке заголовка, сразу за ним: справка страницы, переключатель раскладки. */
    titleAside?: ReactNode;
    /**
     * Чему достаётся класс `pw-header` (шрифт заголовка): `'row'` — всей строке заголовка, его наследует и
     * заголовок-разметка; `'title'` — только строковому заголовку (`span.pw-header`), разметка выводится как есть.
     * С `titleAside` всегда `'title'`, иначе шрифт заголовка унаследуют соседи.
     * @default 'row'
     */
    headerScope?: 'row' | 'title';
    /**
     * Без `actions` не выводить ряд кнопок `.pw-action`. По умолчанию ряд есть всегда: пустой задаёт отступ между
     * заголовком и содержимым.
     * @default false
     */
    omitEmptyActions?: boolean;
    /**
     * Общая рамка, скругление и тень вокруг содержимого, без дополнительной обёртки.
     * Для основного острова страницы; фон, отступы и прокрутку задаёт страница.
     * @default false
     */
    contentSurface?: boolean;
    /** Класс */
    className?: string;
}

const PageWrapper: FC<IPageWrapperProps> = ({
    children,
    title = undefined,
    hideTitle = false,
    titleAside = undefined,
    headerScope = 'row',
    omitEmptyActions = false,
    actions = undefined,
    className = '',
    contentSurface = false,
    ...props
}: IPageWrapperProps) => {
    const Content = contentSurface ? ContentSurface : 'div';
    const titleOnly = headerScope === 'title' || !!titleAside;
    const showHeader = !hideTitle || !!titleAside;
    return (
        <div className={clsx('pw-container', className)} {...props}>
            {showHeader &&
                (titleOnly ? (
                    <div className={clsx('pw-container-row', !!titleAside && 'pw-title-row')}>
                        {typeof title === 'string' ? <span className="pw-header">{title}</span> : title}
                        {titleAside}
                    </div>
                ) : (
                    <div className={clsx('pw-container-row', 'pw-header')}>{title}</div>
                ))}
            {(actions || !omitEmptyActions) && <div className={clsx('pw-container-row', 'pw-action')}>{actions}</div>}
            <Content className={clsx('pw-container-row', 'pw-content')}>{children}</Content>
        </div>
    );
};

export default PageWrapper;
